import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  useUpdateRolePermissions,
  useAddRole,
  useGetUserTypesWithRoles,
} from '@app/graphql';
import {
  UserRoleFormHeader,
  ButtonComponent,
  FormHeading,
  Container,
  SaveButton,
  Seperator,
  RoleDetails,
  UserRoleStatus,
  Row,
  AddUserType,
  RoleName,
  Label,
  Description,
  UserPermissionText,
  Section,
  SectionHeader,
  HeaderText,
  Loader,
  SectionChild,
  SectionSelect,
  ChildText,
  ErrorMessage,
  RoleProperty,
} from '@app/modules/user-roles/user-role-form-component';
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Select,
  TextArea,
  Toast,
} from '@app/components';
import {
  PermissionProps,
  RoleProps,
  SelectedRoleProps,
  UserTypes,
} from '@app/types';
import { Star } from '@app/components/input/input-components';
import DefaultErroPage from '@app/components/error-pages/default-error-page';

type Props = {
  onCancel: () => void;
  loading: boolean;
  userRoleData: any;
  selectedRole: SelectedRoleProps;
  isAdd?: boolean;
  userRoles: any;
  setSelectedRole: any;
  setUserRoles?: any;
};

const defaultSelection = { label: 'Select..', value: '' };
const status = [
  { label: 'ACTIVE', value: 'active' },
  { label: 'INACTIVE', value: 'inactive' },
];
const UserRoleForm = ({
  onCancel,
  loading,
  userRoleData,
  selectedRole,
  isAdd,
  userRoles,
  setSelectedRole,
  setUserRoles,
}: Props) => {
  const { register, errors, handleSubmit, formState } = useForm();
  const { isSubmitted } = formState;
  const {
    loading: userTypeLoading,
    data: userTypeData,
  } = useGetUserTypesWithRoles();
  const [updateRolePermissions] = useUpdateRolePermissions();
  const [addRole] = useAddRole();
  const [saving, updatesaving] = useState<boolean>(false);
  const [showErrorPage, SetShowErrorPage] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showUserType, setShowUserType] = useState(false);
  const [roleData, setRoleData] = useState<RoleProps>(userRoleData);
  const [toastMessage, setToastMessage] = useState('');
  const [userTypes, setUserTypes] = useState<UserTypes[]>([]);
  const [permissions, setPermissions] = useState<PermissionProps[]>(
    userRoleData.permissions,
  );
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768 ? true : false,
  );
  window.addEventListener('resize', function() {
    if (window.innerWidth < 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  });

  useEffect(() => {
    if (
      !userTypeLoading &&
      userTypeData &&
      userTypeData.getUserTypesWithRoles
    ) {
      const { success, userTypes } = userTypeData.getUserTypesWithRoles;
      if (success && userTypes && userTypes.length) {
        setUserTypes(
          userTypes.map((userType: any) => {
            return { ...userType, label: userType.name, value: userType._id };
          }),
        );
      }
    }
  }, [userTypeLoading, userTypeData]);
  useEffect(() => {
    setRoleData({ ...userRoleData, roleId: userRoleData._id });
    setPermissions(userRoleData.permissions);
  }, [userRoleData]);

  const onChange = (event: any, type?: string) => {
    const { name: fieldName, value: fieldValue } =
      (event && event.target) || '';
    if (fieldName === 'roleName') {
      setRoleData({ ...roleData, name: fieldValue });
    } else if (fieldName === 'description') {
      setRoleData({ ...roleData, description: fieldValue });
    } else if (type === 'userType') {
      if (event && event.value && event.value === 'other') {
        setShowUserType(true);
      } else {
        setShowUserType(false);
      }
      setRoleData({ ...roleData, userType: event });
    } else if (fieldName === 'otherUserType') {
      setRoleData({ ...roleData, otherUserType: fieldValue });
    } else if (type === 'status') {
      if (event && event.label) {
        setRoleData({ ...roleData, status: event.label });
      }
    }
  };

  const onCheckBoxChange = (e: any, type: string) => {};

  const onChildCheckBoxChange = (
    e: any,
    type: string,
    parentIndex: number,
    childIndex?: number,
  ) => {
    let perms: PermissionProps[] = [...permissions];
    if (perms && perms.length) {
      let changedPermission: PermissionProps = childIndex
        ? perms[parentIndex].children[childIndex - 1]
        : perms[parentIndex];
      if (changedPermission) {
        changedPermission.option_type =
          type === 'select' ? e.value : e.target.checked ? 1 : 2;
        changedPermission.selectedValue =
          type === 'select' ? e.value : e.target.checked ? 1 : 2;
        changedPermission.selected =
          type === 'select' ? e.label : e.target.checked ? 'Yes' : 'No';
        if (childIndex) {
          perms[parentIndex].children[childIndex - 1] = changedPermission;
        } else {
          perms[parentIndex] = changedPermission;
        }
      }
      if (perms[parentIndex]) {
        let parentPemission: PermissionProps = perms[parentIndex];
        let checkedPermission: PermissionProps[] = parentPemission.children.filter(
          (permission: PermissionProps) => {
            return (
              (permission.type === 'radio' && permission.selectedValue === 1) ||
              (permission.type === 'select' && permission.selectedValue > 0)
            );
          },
        );
        if (checkedPermission && checkedPermission.length) {
          perms[parentIndex].selected = 'Yes';
          perms[parentIndex].selectedValue = 1;
          perms[parentIndex].option_type = 1;
        } else {
          perms[parentIndex].selected = 'No';
          perms[parentIndex].selectedValue = 2;
          perms[parentIndex].option_type = 2;
        }
      }
      setPermissions(perms);
    }
  };

  /**
   *
   * @param message toast message
   */
  const toastDetails = (message: string) => {
    updatesaving(false);
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  /**
   *
   * @param permissions permissions of a user role
   * @returns formatted user role permissions
   */
  const formatPermissions: any = function getPermissions(
    permissions: PermissionProps[],
  ) {
    return permissions && permissions.length
      ? permissions.map((permission: PermissionProps) => {
          let obj = {
            _id: permission._id,
            selectedValue: permission.selectedValue,
          };
          if (permission.children && permission.children.length) {
            Object.assign(obj, {
              children: formatPermissions(permission.children),
            });
          }
          return obj;
        })
      : [];
  };
  const onSubmit = () => {
    updatesaving(true);
    console.log(formatPermissions(permissions));
    let rolePermissionVariable: RoleProps = {
      permissions: isAdd ? [] : formatPermissions(permissions),
      name: roleData.name,
      status: roleData.status || 'ACTIVE',
      description: roleData.description,
      roleId: roleData.roleId,
    };
    if (isAdd) {
      Object.assign(rolePermissionVariable, {
        userTypeName:
          roleData.userType &&
          roleData.userType.value &&
          roleData.userType.value === 'other'
            ? roleData.otherUserType
            : roleData.userType?.label,
      });
      if (
        roleData.userType &&
        roleData.userType.value &&
        roleData.userType.value !== 'other'
      ) {
        Object.assign(rolePermissionVariable, {
          userTypeId: roleData.userType && roleData.userType.value,
          userTypeKey: roleData.userType && roleData.userType.value,
        });
      }
    }
    if (isAdd) {
      addRole({ variables: rolePermissionVariable })
        .then((response: any) => {
          if (response) {
            const { data } = response;
            if (data) {
              const { addNewRole } = data;
              if (addNewRole.success && addNewRole.userRole) {
                const { userRole } = addNewRole;
                setSelectedRole({ name: userRole.name, id: userRole._id });
                updatesaving(false);
                toastDetails('User Role added succesfully');
                setUserRoles({
                  ...userRole,
                  label: userRole.name,
                  value: userRole._id,
                });
              }
            }
          }
        })
        .catch((error: any) => {
          SetShowErrorPage(true);
        });
    } else {
      updateRolePermissions({ variables: rolePermissionVariable })
        .then((result: any) => {
          if (result) {
            updatesaving(false);
            toastDetails('Permissions Updated Succesfully');
          }
        })
        .catch((error: any) => {
          SetShowErrorPage(true);
        });
    }
  };

  return (
    <>
      {loading ? (
        <Loader>
          <Icon name="loader" />
        </Loader>
      ) : showErrorPage ? (
        <DefaultErroPage />
      ) : (
        <>
          {' '}
          <Toast
            message={toastMessage}
            showToast={showToast}
            onClose={() => setShowToast(false)}
          />
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <Container>
              <UserRoleFormHeader>
                <FormHeading>
                  {isMobile ? (
                    <strong onClick={onCancel}>
                      {' '}
                      <Icon name="arrowLeft" />
                    </strong>
                  ) : (
                    ''
                  )}
                  {selectedRole.name ? selectedRole.name : 'Add User Role'}
                </FormHeading>
                <ButtonComponent>
                  <SaveButton>
                    <Button disabled={saving} variant="primary" type="submit">
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </SaveButton>
                </ButtonComponent>
              </UserRoleFormHeader>
              <Seperator />
              <RoleDetails>
                {isAdd ? (
                  <AddUserType>
                    <UserRoleStatus>
                      <RoleProperty>
                        <Label>Select User Type</Label>
                        <Star>*</Star>
                      </RoleProperty>
                      <Select
                        value={roleData.userType}
                        options={userTypes}
                        placeholder="Select User Type"
                        onChange={(e: any) => onChange(e, 'userType')}
                        register={register({
                          required: true,
                          name: 'userType',
                        })}
                      />
                    </UserRoleStatus>
                    {showUserType && (
                      <RoleName>
                        <Input
                          noBottom={true}
                          name="otherUserType"
                          label="User Type"
                          placeholder="Please Enter User Type"
                          star="*"
                          value={roleData.otherUserType}
                          onChange={onChange}
                          register={register({
                            required: 'Please Enter User Type',
                          })}
                        />
                        <ErrorMessage>
                          {isSubmitted &&
                            errors &&
                            errors.roleName &&
                            errors.roleName.message}
                        </ErrorMessage>
                      </RoleName>
                    )}
                  </AddUserType>
                ) : null}
                <Row>
                  <RoleName>
                    <Input
                      name="roleName"
                      label="Role Name"
                      placeholder="Please Enter Role Name"
                      star="*"
                      value={roleData.name}
                      onChange={onChange}
                      register={register({
                        required: 'Please Enter Role Name',
                      })}
                    />
                    <ErrorMessage>
                      {isSubmitted &&
                        errors &&
                        errors.roleName &&
                        errors.roleName.message}
                    </ErrorMessage>
                  </RoleName>
                  <UserRoleStatus>
                    <RoleProperty>
                      <Label>Select Status</Label>
                      <Star>*</Star>
                    </RoleProperty>
                    <Select
                      value={
                        roleData.status
                          ? {
                              label: roleData.status,
                              value: roleData.status,
                            }
                          : defaultSelection
                      }
                      options={status}
                      placeholder="Select Status"
                      onChange={(e: any) => onChange(e, 'status')}
                      register={register({
                        required: true,
                        name: 'roleStatus',
                      })}
                    />
                  </UserRoleStatus>
                </Row>
                <Description>
                  <TextArea
                    label="Description"
                    placeholder="Please Enter Description"
                    value={roleData.description}
                    name="description"
                    onChange={onChange}
                  />
                </Description>
                {!isAdd ? (
                  <>
                    <UserPermissionText>User Permissions</UserPermissionText>
                    {permissions
                      ? permissions.map(
                          (permission: PermissionProps, key: number) => {
                            return (
                              <Section key={key}>
                                <SectionHeader>
                                  <Checkbox
                                    checked={
                                      permission.selectedValue &&
                                      permission.selectedValue === 1
                                        ? true
                                        : false
                                    }
                                    name={permission.key}
                                    onChange={(e: any) =>
                                      onCheckBoxChange(e, permission.key)
                                    }
                                  />
                                  <HeaderText>{permission.name}</HeaderText>
                                </SectionHeader>
                                {permission.children &&
                                permission.children.length
                                  ? permission.children.map(
                                      (
                                        child: PermissionProps,
                                        index: number,
                                      ) => {
                                        return child.type === 'radio' ? (
                                          <SectionChild key={index}>
                                            <Checkbox
                                              noBottom={true}
                                              checked={
                                                child.selectedValue &&
                                                child.selectedValue === 1
                                                  ? true
                                                  : false
                                              }
                                              name={child.key}
                                              onChange={(e: any) =>
                                                onChildCheckBoxChange(
                                                  e,
                                                  'radio',
                                                  key,
                                                  index + 1,
                                                )
                                              }
                                            />
                                            <ChildText>{child.name}</ChildText>
                                          </SectionChild>
                                        ) : child.type === 'select' ? (
                                          <SectionSelect key={index}>
                                            <Select
                                              borderRadius={8}
                                              value={{
                                                label: child.selected,
                                                value: child.selectedValue.toString(),
                                              }}
                                              options={
                                                child.options
                                                  ? child.options
                                                  : []
                                              }
                                              onChange={(e: any) =>
                                                onChildCheckBoxChange(
                                                  e,
                                                  'select',
                                                  key,
                                                  index + 1,
                                                )
                                              }
                                            />
                                          </SectionSelect>
                                        ) : null;
                                      },
                                    )
                                  : null}
                              </Section>
                            );
                          },
                        )
                      : null}
                  </>
                ) : null}
              </RoleDetails>
            </Container>
          </form>
        </>
      )}
    </>
  );
};

export default UserRoleForm;
